import {
  Actions,
  Box,
  ContentBlock,
  Heading,
  Stack,
  Text,
  Tiles,
} from 'braid-design-system';
import type { ComponentProps } from 'react';

import { ButtonLink } from '../Link/Link';

import * as styles from './IconFeatureBlock.css';

type TupleOf<T, N extends number, R extends T[] = []> = R['length'] extends N
  ? R
  : TupleOf<T, N, [T, ...R]> | R;

interface IconColumnProps {
  image: {
    url: string;
    format: 'svg' | 'png' | 'jpg' | undefined;
    width?: string;
    height?: string;
  };
  heading?: string;
  text: string;
  cta?: {
    text: string;
    href: string;
    tracking: string;
    disabled?: boolean;
  };
}

interface IconFeaturesProps {
  heading?: string;
  level?: ComponentProps<typeof Heading>['level'];
  text?: string;
  data: TupleOf<IconColumnProps, 4>;
  width?: ComponentProps<typeof ContentBlock>['width'];
  columns: ComponentProps<typeof Tiles>['columns'];
}

// TODO: Inline this component to reduce crimes
const IconColumn = ({ image, heading, text, cta }: IconColumnProps) => (
  <Box
    height="full"
    style={{ display: 'flex', flexDirection: 'column' }}
    paddingBottom={heading ? undefined : ['small', 'none']}
  >
    <Box
      className={styles.iconImage}
      style={{
        backgroundImage:
          image?.format === 'svg'
            ? `url('data:image/svg+xml;utf8,${image.url.replace(
                /\#/g,
                '%23',
              )}')`
            : `url(${image.url})`,
        maxWidth: image.width || '100%',
        maxHeight: image.height || image.width || '100%',
        margin: 'auto',
      }}
    />
    <Box
      paddingTop={heading ? 'gutter' : ['none', 'gutter']}
      paddingX={['gutter', 'none']}
      textAlign="center"
      height="full"
      display="flex"
      flexDirection="column"
    >
      {heading ? <Text weight="strong">{heading}</Text> : null}
      <Box paddingTop={heading ? 'medium' : ['xsmall', 'medium']}>
        <Text tone="secondary">{text}</Text>
      </Box>
      {cta ? (
        <Box paddingTop="large" display="flex" justifyContent="center">
          {cta?.disabled ? (
            <Box
              padding="xsmall"
              background="neutralLight"
              boxShadow="borderNeutralLight"
              borderRadius="standard"
            >
              <Box padding="xsmall">
                <Text tone="secondary">{cta?.text}</Text>
              </Box>
            </Box>
          ) : (
            <Actions>
              <ButtonLink href={cta.href} tracking={cta.tracking}>
                {cta.text}
              </ButtonLink>
            </Actions>
          )}
        </Box>
      ) : null}
    </Box>
  </Box>
);

export const IconFeatureBlock = ({
  heading,
  level = '2',
  text,
  data,
  width,
  columns,
}: IconFeaturesProps) => (
  <Box>
    <ContentBlock width={width}>
      <Stack space="xlarge" align="center">
        <ContentBlock>
          <Box paddingX="large">
            <Stack space="large" align="center">
              {heading ? (
                <Heading level={level} align="center">
                  {heading}
                </Heading>
              ) : null}
              {text ? (
                <Box maxWidth="medium">
                  <Text align="center" size="large" tone="secondary">
                    {text}
                  </Text>
                </Box>
              ) : null}
            </Stack>
          </Box>
        </ContentBlock>
        <Tiles space={{ mobile: 'large', desktop: 'gutter' }} columns={columns}>
          {data.map((item, key) => (
            <IconColumn {...item} key={key} />
          ))}
        </Tiles>
      </Stack>
    </ContentBlock>
  </Box>
);
